import * as React from 'react';
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom';

/**
 * NavigationGuardContext
 * ----------------------
 * Lightweight, BrowserRouter-friendly replacement for react-router's
 * useBlocker (which requires a Data Router setup the rest of the app
 * doesn't have). Any feature with unsaved state — today only the new-
 * order wizard — registers a `confirmLeave` handler with this context.
 * Every navigation surface in AppShell (sidebar, logo, +New order CTA,
 * avatar / Settings link, sign-out, notifications popover) then calls
 * `guard.confirmLeave()` (via the `useGuardedClick` / `useGuardedNavigate`
 * helpers below) before letting the navigation happen.
 *
 * The handler is a `() => Promise<boolean>` — true means "go ahead",
 * false means "I asked the user; they said stay here". When no handler
 * is registered (e.g. wizard isn't mounted) confirmLeave resolves to
 * true immediately, so call sites stay cheap to add.
 */

export type ConfirmLeaveHandler = () => Promise<boolean>;

interface NavigationGuardValue {
  /** Most-recently published dirty flag. Used by guarded helpers to skip
   *  the handler call when there's nothing to ask about. */
  isDirty: boolean;
  /** The wizard / feature publishes its dirty state here so the guard
   *  can short-circuit when nothing is at risk. */
  setDirty: (dirty: boolean) => void;
  /** Register a leave-confirmation handler. Returns an unregister function
   *  that the caller MUST run on unmount. Only one handler is supported
   *  at a time — registering a second handler replaces the first. */
  registerHandler: (handler: ConfirmLeaveHandler) => () => void;
  /** Call before navigating away. Resolves true if the navigation should
   *  proceed, false if the user asked to stay. */
  confirmLeave: () => Promise<boolean>;
}

const NavigationGuardContext = React.createContext<NavigationGuardValue | null>(null);

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const [isDirty, setDirtyState] = React.useState(false);
  // Internal refs so the public callbacks below stay reference-stable
  // across re-renders (every dirty-flag toggle would otherwise re-trigger
  // any consumer effect with `[guard]` deps — see e.g. the wizard's
  // handler register loop bug from the review).
  const isDirtyRef = React.useRef(isDirty);
  React.useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  const handlerRef = React.useRef<ConfirmLeaveHandler | null>(null);

  const setDirty = React.useCallback((next: boolean) => {
    setDirtyState((prev) => (prev === next ? prev : next));
  }, []);

  const registerHandler = React.useCallback((handler: ConfirmLeaveHandler) => {
    handlerRef.current = handler;
    return () => {
      if (handlerRef.current === handler) handlerRef.current = null;
    };
  }, []);

  // Stable — reads the latest isDirty + handler via refs. This means
  // useGuardedNavigate / useGuardedLinkClick / useGuardedAction can
  // destructure confirmLeave and depend on it without re-creating their
  // own callbacks every time the wizard toggles dirty.
  const confirmLeave = React.useCallback(async () => {
    if (!isDirtyRef.current || !handlerRef.current) return true;
    return handlerRef.current();
  }, []);

  const value = React.useMemo<NavigationGuardValue>(
    () => ({ isDirty, setDirty, registerHandler, confirmLeave }),
    [isDirty, setDirty, registerHandler, confirmLeave],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

/**
 * Read the guard. When no provider is mounted (e.g. inside the auth
 * layout or stand-alone tests) the hook returns a permissive no-op so
 * call sites don't have to branch.
 */
export function useNavigationGuard(): NavigationGuardValue {
  const ctx = React.useContext(NavigationGuardContext);
  if (ctx) return ctx;
  return {
    isDirty: false,
    setDirty: () => undefined,
    registerHandler: () => () => undefined,
    confirmLeave: () => Promise.resolve(true),
  };
}

/**
 * Drop-in replacement for `useNavigate` that asks the guard first. Used
 * by buttons that programmatically navigate (e.g. AppShell's +New order
 * CTA, NotificationsPopover row click).
 */
export function useGuardedNavigate(): (
  to: To,
  options?: NavigateOptions,
) => Promise<void> {
  const navigate = useNavigate();
  // Depend on the stable confirmLeave callback (not the whole guard
  // object) so this hook's returned function stays reference-stable
  // across dirty-flag toggles.
  const { confirmLeave } = useNavigationGuard();
  return React.useCallback(
    async (to, options) => {
      const ok = await confirmLeave();
      if (ok) navigate(to, options);
    },
    [navigate, confirmLeave],
  );
}

/**
 * Click interceptor for plain `<Link>` / `<NavLink>` elements. Wires up
 * an onClick that respects modifier keys (Ctrl/Cmd/Shift/middle-click =>
 * open-in-new-tab, never blocked) and otherwise asks the guard before
 * letting the browser follow the link.
 *
 * Usage:
 *   const onClick = useGuardedLinkClick('/orders');
 *   <Link to="/orders" onClick={onClick}>Orders</Link>
 */
export function useGuardedLinkClick(
  to: To | null | undefined,
): (event: React.MouseEvent<HTMLAnchorElement>) => void {
  const navigate = useNavigate();
  const { confirmLeave } = useNavigationGuard();
  return React.useCallback(
    (event) => {
      if (event.defaultPrevented) return;
      // Let the user open in a new tab / window without prompting.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      void (async () => {
        const ok = await confirmLeave();
        if (ok && to != null) navigate(to);
      })();
    },
    [navigate, confirmLeave, to],
  );
}

/**
 * For nav exits that aren't router-driven — e.g. sign-out's
 * `window.location.href = '/auth'`. Caller passes a `proceed` thunk
 * that runs the hard navigation; the hook returns an async function
 * that asks the guard first.
 */
export function useGuardedAction(proceed: () => void): () => Promise<void> {
  const { confirmLeave } = useNavigationGuard();
  return React.useCallback(async () => {
    const ok = await confirmLeave();
    if (ok) proceed();
  }, [confirmLeave, proceed]);
}
